import { useEffect, useState } from 'react';
import { useKeycloak } from '@react-keycloak/web';
import { useUnit } from 'effector-react';
import toast from 'react-hot-toast';
import { getRequestList } from '@/api/requests';
import { $user } from '@/models/user';
import type { RequestListType } from '@/types/request';
import { GroupsEnum } from '@/types/users';

type DashboardCounts = Record<RequestListType, number | null>;

const emptyCounts: DashboardCounts = {
  work: null,
  approve: null,
  close: null,
  draft: null,
};

const draftGroups = new Set<string>([
  GroupsEnum.ADMIN,
  GroupsEnum.CUSTOMER,
  GroupsEnum.CUSTOMER_DC,
  GroupsEnum.BACKUP_LEADER,
  GroupsEnum.REGIONAL_DIRECTOR,
]);

const normalizeGroup = (group: string) => group.trim().toLocaleLowerCase('ru-RU');

const SUBORDINATES_STORAGE_KEY = 'showSubordinates';
const MANAGER_REQUESTS_STORAGE_KEY = 'showManagerDepartmentRequests';

const readStoredFilter = (key: string) => localStorage.getItem(key) === '1';

const DashboardPage = () => {
  const { keycloak } = useKeycloak();
  const user = useUnit($user);
  const [counts, setCounts] = useState<DashboardCounts>(emptyCounts);
  const [loading, setLoading] = useState(true);
  const [showSubordinates, setShowSubordinates] = useState(() =>
    readStoredFilter(SUBORDINATES_STORAGE_KEY),
  );
  const [showManagerDepartmentRequests, setShowManagerDepartmentRequests] = useState(() =>
    readStoredFilter(MANAGER_REQUESTS_STORAGE_KEY),
  );

  const canViewDrafts =
    user?.groups?.some((group) => draftGroups.has(normalizeGroup(group))) ?? false;

  useEffect(() => {
    if (!keycloak.token) return;

    let active = true;
    setLoading(true);
    setCounts(emptyCounts);

    const listTypes: RequestListType[] = canViewDrafts
      ? ['work', 'approve', 'close', 'draft']
      : ['work', 'approve', 'close'];

    const filters = {
      subordinates: showSubordinates,
      managerDepartmentRequests: showManagerDepartmentRequests,
    };

    Promise.allSettled(listTypes.map((type) => getRequestList(type, keycloak.token, filters)))
      .then((results) => {
        if (!active) return;

        const nextCounts: DashboardCounts = {
          ...emptyCounts,
          draft: canViewDrafts ? null : 0,
        };
        const errors: string[] = [];

        results.forEach((result, index) => {
          const type = listTypes[index];
          if (result.status === 'fulfilled') {
            nextCounts[type] = Number(result.value.count);
          } else {
            errors.push(
              result.reason instanceof Error ? result.reason.message : String(result.reason),
            );
          }
        });

        setCounts(nextCounts);
        if (errors.length) toast.error(Array.from(new Set(errors)).join('\n'));
      })
      .finally(() => active && setLoading(false));

    return () => {
      active = false;
    };
  }, [canViewDrafts, keycloak.token, showManagerDepartmentRequests, showSubordinates]);

  const updateFilter = (key: string, checked: boolean, setter: (value: boolean) => void) => {
    setter(checked);
    localStorage.setItem(key, checked ? '1' : '0');
  };

  const availableCounts = [counts.work, counts.approve, counts.close, counts.draft];
  const total = availableCounts.every((value) => value !== null)
    ? availableCounts.reduce<number>((sum, value) => sum + (value ?? 0), 0)
    : null;

  const summary = [
    { label: 'Всего заявок', value: total, href: '/hr/list' },
    { label: 'В работе', value: counts.work, href: '/hr/list' },
    { label: 'На согласовании', value: counts.approve, href: '/hr/list' },
    { label: 'Закрытые', value: counts.close, href: '/hr/list' },
    { label: 'Черновики', value: counts.draft, href: '/hr/list/draft' },
  ];

  return (
    <div className="w-full text-gray-800">
      <section className="mb-5 rounded-xl border border-gray-200 bg-white px-5 py-5 shadow-sm md:px-6">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h2 className="text-xl font-semibold text-gray-900 md:text-2xl">Заявки</h2>
            <p className="mt-1 text-sm text-gray-500">
              Сводная информация по доступным вам заявкам.
            </p>
          </div>

          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-6">
            <label className="flex cursor-pointer items-center gap-2 text-sm text-gray-600">
              <input
                type="checkbox"
                className="h-4 w-4 cursor-pointer accent-[#631d76]"
                checked={showSubordinates}
                onChange={(event) =>
                  updateFilter(SUBORDINATES_STORAGE_KEY, event.target.checked, setShowSubordinates)
                }
              />
              <span>Заявки подчинённых</span>
            </label>

            <label className="flex cursor-pointer items-center gap-2 text-sm text-gray-600">
              <input
                type="checkbox"
                className="h-4 w-4 cursor-pointer accent-[#631d76]"
                checked={showManagerDepartmentRequests}
                onChange={(event) =>
                  updateFilter(
                    MANAGER_REQUESTS_STORAGE_KEY,
                    event.target.checked,
                    setShowManagerDepartmentRequests,
                  )
                }
              />
              <span>Заявки руководителя по моему подразделению</span>
            </label>
          </div>
        </div>
      </section>

      <section className="mb-5 grid gap-4 sm:grid-cols-2 xl:grid-cols-5">
        {summary.map((item) => (
          <a
            key={item.label}
            href={item.href}
            className="block rounded-xl border border-gray-200 bg-white p-5 text-gray-800 no-underline shadow-sm transition hover:-translate-y-0.5 hover:border-[#631d76] hover:shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-[#631d76]"
          >
            <p className="text-sm font-medium text-gray-500">{item.label}</p>
            <p className="mt-3 text-3xl font-semibold text-gray-900">
              {loading || item.value === null ? '—' : item.value}
            </p>
          </a>
        ))}
      </section>
    </div>
  );
};

export default DashboardPage;
